import { useEffect, useState, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { LogOut, RefreshCw, Trash2, Inbox, Loader2 } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Admin() {
  const [enquiries, setEnquiries] = useState(null);
  const navigate = useNavigate();
  const token = localStorage.getItem("ani-admin-token");

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/enquiries`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setEnquiries(data);
    } catch (err) {
      if (err.response?.status === 401) {
        localStorage.removeItem("ani-admin-token");
        navigate("/admin/login");
      } else {
        toast.error("Could not load enquiries.");
        setEnquiries([]);
      }
    }
  }, [token, navigate]);

  useEffect(() => {
    if (!token) {
      navigate("/admin/login");
      return;
    }
    load();
  }, [token, navigate, load]);

  const remove = async (id) => {
    if (!window.confirm("Delete this enquiry?")) return;
    try {
      await axios.delete(`${API}/enquiries/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setEnquiries((list) => list.filter((e) => e.id !== id));
      toast.success("Enquiry deleted.");
    } catch {
      toast.error("Delete failed.");
    }
  };

  const logout = () => {
    localStorage.removeItem("ani-admin-token");
    navigate("/admin/login");
  };

  return (
    <main data-testid="admin-dashboard" className="min-h-[100svh] bg-[#09121F] px-5 pb-20 pt-28 sm:px-8">
      <div className="mx-auto max-w-6xl">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <img src="/assets/logo-mark.png" alt="Al Nama Importer" className="h-12 w-auto object-contain" />
            <div>
              <h1 className="font-serif text-2xl font-semibold text-emerald-50">Enquiries Dashboard</h1>
              <p className="text-xs uppercase tracking-[0.25em] text-slate-500">
                {enquiries ? `${enquiries.length} enquiries` : "Loading..."}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button
              data-testid="admin-refresh-btn"
              onClick={load}
              className="inline-flex items-center gap-2 rounded-md border border-amber-500/30 px-4 py-2.5 font-display text-xs font-semibold uppercase tracking-wider text-amber-400 transition-colors hover:bg-amber-500/10"
            >
              <RefreshCw className="h-3.5 w-3.5" /> Refresh
            </button>
            <button
              data-testid="admin-logout-btn"
              onClick={logout}
              className="inline-flex items-center gap-2 rounded-md border border-red-500/30 px-4 py-2.5 font-display text-xs font-semibold uppercase tracking-wider text-red-300 transition-colors hover:bg-red-500/10"
            >
              <LogOut className="h-3.5 w-3.5" /> Logout
            </button>
          </div>
        </div>

        <div className="mt-10 space-y-4">
          {enquiries === null && (
            <div className="flex justify-center py-20">
              <Loader2 className="h-8 w-8 animate-spin text-amber-400" />
            </div>
          )}
          {enquiries && enquiries.length === 0 && (
            <div className="flex flex-col items-center rounded-xl border border-amber-500/15 bg-[#0F1E18]/50 py-20 text-center">
              <Inbox className="h-10 w-10 text-slate-600" strokeWidth={1.2} />
              <p className="mt-4 text-sm text-slate-400">No enquiries yet. New submissions will appear here.</p>
            </div>
          )}
          {enquiries &&
            enquiries.map((e, i) => (
              <motion.div
                key={e.id}
                data-testid="admin-enquiry-row"
                initial={{ opacity: 0, y: 14 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: Math.min(i * 0.05, 0.4) }}
                className="rounded-lg border border-amber-500/15 bg-[#0F1E18]/60 p-6"
              >
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div>
                    <div className="flex flex-wrap items-center gap-3">
                      <h3 className="font-serif text-lg font-semibold text-emerald-50">{e.full_name}</h3>
                      <span className="rounded-full border border-amber-500/30 bg-amber-500/10 px-3 py-1 text-[10px] font-semibold uppercase tracking-wider text-amber-400">
                        {e.enquiry_type}
                      </span>
                      {e.product && (
                        <span className="rounded-full border border-emerald-500/25 bg-emerald-500/10 px-3 py-1 text-[10px] font-semibold uppercase tracking-wider text-emerald-300">
                          {e.product}
                        </span>
                      )}
                    </div>
                    <p className="mt-1.5 text-xs text-slate-500">
                      {[e.company_name, e.country].filter(Boolean).join(" · ") || "—"}
                      {" · "}
                      {e.created_at ? new Date(e.created_at).toLocaleString() : ""}
                    </p>
                  </div>
                  <button
                    data-testid={`admin-delete-btn-${e.id}`}
                    onClick={() => remove(e.id)}
                    aria-label="Delete enquiry"
                    className="rounded-md border border-red-500/25 p-2 text-red-400/70 transition-colors hover:border-red-400 hover:text-red-300"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
                <div className="mt-4 flex flex-wrap gap-x-8 gap-y-2 text-sm">
                  <a href={`mailto:${e.email}`} className="text-amber-400/90 hover:underline">{e.email}</a>
                  <a href={`tel:${e.phone}`} className="text-slate-300 hover:text-amber-400">{e.phone}</a>
                </div>
                <p className="mt-4 rounded-md bg-[#09121F]/70 p-4 text-sm font-light leading-relaxed text-slate-300/90">
                  {e.message}
                </p>
              </motion.div>
            ))}
        </div>

        <p className="mt-10 text-center">
          <Link to="/" className="text-xs text-slate-500 transition-colors hover:text-amber-400">
            ← Back to website
          </Link>
        </p>
      </div>
    </main>
  );
}
